// =============================================================================
// screens/VoiceRoomScreen.tsx — Live voice room (LiveKit-backed audio/video stage)
//
// Bare-RN port of apps/cryb-native/app/voice/[id].tsx (P3-5 CALLING group).
//
// MIGRATION (blueprint §1):
//   - expo-router (useLocalSearchParams/useRouter/Stack.Screen)
//       -> react-navigation: route.params.id (typed RootStackParamList['VoiceRoom'])
//          + useNavigation().goBack(). The native header is now HIDDEN — the room
//          renders its own translucent floating GlassSurface header so the stage
//          (ParticleBackground + grid) scrolls UNDER it (Apple design §12).
//   - @expo/vector-icons (Ionicons) -> components/ui/Icon.
//   - @livekit/react-native works in bare RN AS-IS (registerGlobals() runs in
//     index.js BEFORE App). AudioSession + LiveKitRoom + room/participant/track
//     hooks (useParticipants/useTracks/useRoomContext) unchanged.
//
// PRESERVED VERBATIM (this is a visual/UX reskin only — every LiveKit / track /
// teardown / reaction / screenshare / sheet code path is byte-for-byte the same):
//   - join via POST /voice/rooms/:id/join (scopes the token to custom_room_<id>),
//   - participant grid + per-tile camera/avatar/speaking-ring/mute-badge,
//   - mic/cam optimistic toggles (commit after the SDK call resolves),
//   - AudioSession start-on-mount / stop-on-unmount, LiveKit disconnect = leave.
//
// REDESIGN (Apple-aligned, dark-mode glass over ParticleBackground):
//   - one continuous depth-based surface across joining / live / speaking / muted
//     / ended / leaving / PiP, all sharing the same material + spatial anchor,
//   - translucent floating header (chevron-down = minimize-to-PiP, X = leave),
//   - animated LIVE pulse + interruptible speaking ring (reanimated spring),
//   - GlassSurface control bar with an unmistakable own-mute state + separated
//     destructive Leave,
//   - designed ENDED card (host closed the room) + LEAVING + JOINING cards,
//   - PiP via the shipped CallInProgressBanner (minimize keeps the room connected).
// =============================================================================
import { useEffect, useState, useCallback, useMemo, useLayoutEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  useWindowDimensions,
  Platform,
  NativeModules,
  findNodeHandle,
  Animated,
  Easing,
} from 'react-native';
import Reanimated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  withSpring,
  cancelAnimation,
  Easing as REasing,
} from 'react-native-reanimated';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets, type EdgeInsets } from 'react-native-safe-area-context';
import {
  LiveKitRoom,
  VideoTrack,
  useParticipants,
  useTracks,
  useRoomContext,
  isTrackReference,
  type TrackReference,
} from '@livekit/react-native';
import { ScreenCapturePickerView } from '@livekit/react-native-webrtc';
import { Track, RoomEvent, type Participant } from 'livekit-client';
import { Ionicons } from '../components/ui/Icon';
import type { RootStackParamList } from '../navigation/types';
import {
  colors,
  colorTokens,
  spacing,
  radius,
  fonts,
  getLayout,
  typographyScale,
  animationTokens,
} from '../constants/theme';
import ParticleBackground from '../components/ui/ParticleBackground';
import Asset3D from '../components/ui/Asset3D';
import { GlassSurface, GlassCard, GlassButton, glassTokens } from '../components/glass';
import { tabularNums } from '../lib/format';
import { api } from '../services/api';
import { onUserEvent } from '../services/socket';
import {
  ensureLiveKitGlobals,
  prepareVoiceAudio,
  scheduleVoiceAudioStop,
  runGuardedTeardown,
} from '../services/livekit-globals';
import { notifyScreenShareStart, notifyScreenShareStop } from '../services/screenShare';
import ConnectionBanner from '../components/voice/ConnectionBanner';
import ConnectionQualityBadge from '../components/voice/ConnectionQualityBadge';
import ParticipantSheet from '../components/voice/ParticipantSheet';
import AddParticipantsSheet from '../components/call/AddParticipantsSheet';
import { setActiveCall, clearActiveCall } from '../components/call/CallInProgressBanner';
import Safe from '../components/Safe';

// NOTE: ensureLiveKitGlobals() is intentionally NOT called at module scope. Running
// the WebRTC native init while Hermes EVALUATES this module (during the lazy
// require in App.tsx) can throw in a release archive → the module record is left
// undefined → `require(...).default` throws "Cannot read property 'default' of
// undefined" and crashes navigation. It is now called INSIDE the component (mount
// effect), so module-eval has no native side-effect.

const HEADER_HEIGHT = 52; // floating glass header content height (below the notch)

// ── In-call emoji reactions (data-channel) ────────────────────────────────────
const REACTION_EMOJI = ['❤️', '😂', '👍', '🔥', '👏', '🎉'] as const;
const REACTION_TOPIC = 'cryb-reaction';

// UTF-8 (de)serialization without relying on TextEncoder/TextDecoder (not
// guaranteed polyfilled in RN). Safe for the short JSON reaction packets.
function encodeUtf8(str: string): Uint8Array {
  const bin = unescape(encodeURIComponent(str));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}
function decodeUtf8(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return decodeURIComponent(escape(bin));
}

interface FloatingReaction { id: string; emoji: string; }

// Publish/receive reactions over the room's data channel. Used INSIDE <LiveKitRoom>.
function useReactions(roomCtx: ReturnType<typeof useRoomContext>) {
  const [reactions, setReactions] = useState<FloatingReaction[]>([]);
  const seq = useRef(0);
  const push = useCallback((emoji: string) => {
    const id = `${Date.now()}-${seq.current++}`;
    setReactions((prev) => [...prev, { id, emoji }]);
  }, []);
  const remove = useCallback((id: string) => {
    setReactions((prev) => prev.filter((r) => r.id !== id));
  }, []);
  useEffect(() => {
    if (!roomCtx) return;
    const onData = (payload: Uint8Array, _p: any, _k: any, topic?: string) => {
      if (topic && topic !== REACTION_TOPIC) return;
      try {
        const msg = JSON.parse(decodeUtf8(payload));
        if (msg?.type === 'reaction' && typeof msg.emoji === 'string') push(msg.emoji);
      } catch { /* ignore non-reaction / malformed packets */ }
    };
    roomCtx.on(RoomEvent.DataReceived, onData as any);
    return () => { try { roomCtx.off(RoomEvent.DataReceived, onData as any); } catch {} };
  }, [roomCtx, push]);
  const send = useCallback((emoji: string) => {
    push(emoji);
    try {
      const data = encodeUtf8(JSON.stringify({ type: 'reaction', emoji }));
      void roomCtx?.localParticipant?.publishData(data as any, { reliable: false, topic: REACTION_TOPIC });
    } catch (e) { console.warn('[livekit] publish reaction failed:', e); }
  }, [roomCtx, push]);
  return { reactions, send, remove };
}

// One rising emoji — floats up + fades, then self-removes.
function RisingEmoji({ emoji, onDone }: { emoji: string; onDone: () => void }) {
  const t = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(t, {
      toValue: 1, duration: 2200, easing: Easing.out(Easing.quad), useNativeDriver: true,
    }).start(() => onDone());
  }, [t, onDone]);
  const drift = useRef((Math.random() - 0.5) * 60).current;
  return (
    <Animated.Text
      style={[
        styles.risingEmoji,
        {
          opacity: t.interpolate({ inputRange: [0, 0.15, 0.85, 1], outputRange: [0, 1, 1, 0] }),
          transform: [
            { translateY: t.interpolate({ inputRange: [0, 1], outputRange: [0, -220] }) },
            { translateX: t.interpolate({ inputRange: [0, 1], outputRange: [0, drift] }) },
            { scale: t.interpolate({ inputRange: [0, 0.2, 1], outputRange: [0.6, 1.2, 1] }) },
          ],
        },
      ]}
    >
      {emoji}
    </Animated.Text>
  );
}

function ReactionOverlay({ reactions, onDone }: { reactions: FloatingReaction[]; onDone: (id: string) => void }) {
  return (
    <View style={styles.reactionOverlay} pointerEvents="none">
      {reactions.map((r) => (
        <RisingEmoji key={r.id} emoji={r.emoji} onDone={() => onDone(r.id)} />
      ))}
    </View>
  );
}

// Reactions row — same dark glass material as the control bar so the surface matches.
function ReactionsRow({ onSend }: { onSend: (emoji: string) => void }) {
  return (
    <GlassSurface
      variant="pill"
      mode="dark"
      radius={radius.full}
      contentStyle={styles.reactionsRow}
    >
      {REACTION_EMOJI.map((e) => (
        <TouchableOpacity
          key={e}
          style={styles.reactionBtn}
          onPress={() => onSend(e)}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={`React with ${e}`}
        >
          <Text style={styles.reactionEmoji}>{e}</Text>
        </TouchableOpacity>
      ))}
    </GlassSurface>
  );
}

// Interruptible pulsing LIVE dot (reanimated). Scales + fades on a loop; the
// spring-nature of withTiming loops reads as a soft heartbeat, not a hard blink.
function LiveDot({ color = colors.primaryLight, size = 7 }: { color?: string; size?: number }) {
  const p = useSharedValue(0);
  useEffect(() => {
    p.value = withRepeat(withTiming(1, { duration: 900, easing: REasing.inOut(REasing.ease) }), -1, true);
    return () => cancelAnimation(p);
  }, [p]);
  const aStyle = useAnimatedStyle(() => ({
    transform: [{ scale: 1 + p.value * 0.4 }],
    opacity: 1 - p.value * 0.45,
  }));
  return (
    <Reanimated.View
      style={[{ width: size, height: size, borderRadius: size / 2, backgroundColor: color }, aStyle]}
    />
  );
}

// Soft pulsing mic — the shared "connecting" affordance for joining / empty grid.
function PulsingMic({ size = 30, color = colors.primaryLight }: { size?: number; color?: string }) {
  const p = useSharedValue(0);
  useEffect(() => {
    p.value = withRepeat(withTiming(1, { duration: 1100, easing: REasing.inOut(REasing.ease) }), -1, true);
    return () => cancelAnimation(p);
  }, [p]);
  const aStyle = useAnimatedStyle(() => ({
    transform: [{ scale: 1 + p.value * 0.12 }],
    opacity: 0.6 + p.value * 0.4,
  }));
  return (
    <Reanimated.View style={aStyle}>
      <Ionicons name="mic" size={size} color={color} />
    </Reanimated.View>
  );
}

// Interruptible speaking ring. On isSpeaking=true it loops a gentle scale+glow
// (Apple §3-4); on false it springs back to rest (no hard on/off pop).
function SpeakingRing({ active, radius: r }: { active: boolean; radius: number }) {
  const s = useSharedValue(1);
  const glow = useSharedValue(0);
  useEffect(() => {
    if (active) {
      s.value = withRepeat(withTiming(1.08, { duration: 700, easing: REasing.inOut(REasing.ease) }), -1, true);
      glow.value = withRepeat(withTiming(1, { duration: 700, easing: REasing.inOut(REasing.ease) }), -1, true);
    } else {
      cancelAnimation(s);
      cancelAnimation(glow);
      s.value = withSpring(1, animationTokens.spring);
      glow.value = withSpring(0, animationTokens.spring);
    }
    return () => { cancelAnimation(s); cancelAnimation(glow); };
  }, [active, s, glow]);
  const aStyle = useAnimatedStyle(() => ({
    transform: [{ scale: s.value }],
    opacity: 0.55 + glow.value * 0.45,
  }));
  if (!active) return null;
  return (
    <Reanimated.View
      pointerEvents="none"
      style={[
        styles.speakingRing,
        { borderRadius: r + 4, shadowColor: colors.success, shadowOpacity: 0.5, shadowRadius: 8 },
        aStyle,
      ]}
    />
  );
}

// Fallback shown if RoomBody's native views fail to render (see the <Safe> wrap).
// A recoverable in-room error with a Leave button — never a blanked app.
function RoomRenderFallback({ onLeave }: { onLeave: () => void }) {
  return (
    <View style={styles.stateRoot}>
      <ParticleBackground style={StyleSheet.absoluteFill} theme="light" count={5} />
      <GlassCard style={styles.stateCard} padding={spacing.xl}>
        <Asset3D name="error" size={96} style={{ marginBottom: spacing.sm }} />
        <Text style={styles.stateTitle}>Couldn't open this room</Text>
        <Text style={styles.stateSub}>Something went wrong loading the live view. Your account is fine.</Text>
        <GlassButton title="Leave" onPress={onLeave} fullWidth style={{ marginTop: spacing.lg }} />
      </GlassCard>
    </View>
  );
}

type Nav = NativeStackNavigationProp<RootStackParamList, 'VoiceRoom'>;
type VoiceRoomRoute = RouteProp<RootStackParamList, 'VoiceRoom'>;

// LiveKit connection material returned by the join endpoint.
interface VoiceToken {
  token: string;
  url: string;
  roomName: string;
}

// Cosmetic room metadata; all fields optional since the screen works token-only.
interface RoomDetail {
  id: string;
  title: string;
  topic?: string;
  host?: {
    username: string;
    display_name: string;
  };
  participants_count?: number;
}

export default function VoiceRoomScreen() {
  const navigation = useNavigation<Nav>();
  const { params } = useRoute<VoiceRoomRoute>();
  const id = params?.id ?? '';
  const insets = useSafeAreaInsets();

  // Hide the native opaque header — the room paints its own translucent floating
  // GlassSurface header so content flows UNDER it (Apple design §12).
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  // Responsive sizing: on tablet/desktop the grid is centred and width-capped and
  // the participant grid ADDS columns as the stage widens (phone 3 → tablet 4 →
  // desktop 5) instead of stretching a fixed 3-up. participantSize is the per-cell
  // width for that column count after the row gutters (spacing.lg edges + spacing.md
  // gaps between the numColumns cells).
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const gridMaxWidth = layout === 'desktop' ? 880 : 640;
  const gridWidth = layout !== 'phone' ? gridMaxWidth : width;
  const gridColumns = layout === 'desktop' ? 5 : layout === 'tablet' ? 4 : 3;
  const participantSize = Math.floor(
    (gridWidth - spacing.lg * 2 - spacing.md * (gridColumns - 1)) / gridColumns,
  );
  const containerStyle = layout !== 'phone'
    ? { maxWidth: gridMaxWidth, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;

  const [room, setRoom] = useState<RoomDetail | null>(null);
  const [connection, setConnection] = useState<VoiceToken | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Stage mode (from the join response): whether this is a stage room and whether
  // I'm its host. Drives the listener "Request to speak" + host approve UI.
  const [stage, setStage] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  // Fetch optional room metadata (title/topic) — non-fatal if it fails.
  // GET /voice/rooms/:id returns { room: { id, name, displayName, ... }, participants }
  // (NOT the flat RoomDetail shape), so map title←displayName/name from res.data.room.
  const fetchRoomMeta = useCallback(async () => {
    const res = await api<{ room?: any; participants?: any[] }>(`/voice/rooms/${id}`);
    if (res.success && res.data?.room) {
      const r = res.data.room;
      setRoom({
        ...r,
        title: r.displayName ?? r.name ?? 'Voice Room',
      });
    }
  }, [id]);

  // Fetch the LiveKit access token + server url. This is required to connect.
  const fetchToken = useCallback(async () => {
    setError(null);
    setLoading(true);
    // Use the join endpoint, NOT GET /voice/token: join scopes the token to the
    // real LiveKit room (`custom_room_<id>`). GET /voice/token used the bare id,
    // so two users silently landed in different auto-created empty rooms.
    try {
      const res = await api<any>(`/voice/rooms/${id}/join`, {
        method: 'POST',
        body: JSON.stringify({ videoEnabled: false, audioEnabled: true }),
      });
      const token = res.data?.token;
      const url = res.data?.liveKitUrl || res.data?.url;
      if (res.success && token && url) {
        setConnection({ token, url, roomName: res.data?.roomName });
        setStage(!!res.data?.stage);
        setIsOwner(!!res.data?.isOwner);
      } else {
        setError(res.error || 'Could not get a voice token for this room');
      }
    } catch (e: any) {
      setError(e?.message || 'Could not connect to this room');
    } finally {
      // Guaranteed so we always leave the loading state (→ error UI, never blank).
      setLoading(false);
    }
  }, [id]);

  // AUDIO ROOT-CAUSE FIX: configure the AVAudioSession (playAndRecord +
  // voiceChat, speaker default) BEFORE activating it and BEFORE the room
  // connects. The bare startAudioSession() this replaced only did
  // setActive(true) without setting the session category → silent rooms.
  const [audioReady, setAudioReady] = useState(false);

  // Bootstrap on mount: load metadata + token and bring up the native audio
  // session for the lifetime of this screen.
  useEffect(() => {
    // Register WebRTC globals HERE (not at module scope) — on first room open,
    // inside the component, so evaluating this module never touches native.
    ensureLiveKitGlobals();
    fetchRoomMeta();
    fetchToken();
    let alive = true;
    prepareVoiceAudio('room').finally(() => {
      if (alive) setAudioReady(true);
    });
    return () => {
      alive = false;
      // CRASH FIX (teardown order): never setActive(false) while <LiveKitRoom>
      // is still disconnecting. The explicit leave flow (RoomBody) stops the
      // session after disconnect resolves; this DELAYED stop only covers exits
      // that bypass it, and no-ops if the session was already stopped.
      scheduleVoiceAudioStop();
      // No participant-leave endpoint exists for custom voice rooms:
      //   POST /voice/rooms/:id/leave    → 404 (Route not found)
      //   POST /voice/channels/:id/leave → 404 (Voice state not found — wrong id space)
      //   DELETE /voice/rooms/:id        → CLOSES the room for everyone (owner-only action)
      // The actual leave is handled by LiveKit (room disconnect on unmount / doLeave),
      // so there is no extra REST call to make here. Kept as a best-effort no-op.
    };
  }, [fetchRoomMeta, fetchToken, id]);

  // Surface LiveKit connection failures inline so the user gets a Retry path.
  const handleRoomError = useCallback((e: Error) => {
    console.warn('[livekit] room error:', e);
    setError(e?.message || 'Connection error');
  }, []);

  // --- Render states: loading / error / connected ---------------------------

  // Also hold the room mount until the audio session is configured (the
  // canonical order is configure → activate → connect).
  // Guaranteed audio-session teardown for any exit from the pre-connect states.
  const goBack = useCallback(() => {
    try { scheduleVoiceAudioStop(); } catch {}
    navigation.goBack();
  }, [navigation]);

  // JOINING — ParticleBackground + centered dark GlassCard with a pulsing mic;
  // the spinner is anchored where the room title will appear so the transition
  // into the live stage feels spatially continuous (Apple §7).
  if (loading || !audioReady) {
    return (
      <View style={styles.stateRoot}>
        <ParticleBackground theme="dark" style={StyleSheet.absoluteFill} />
        <View style={styles.stateCenter}>
          <GlassCard mode="dark" style={styles.stateCard} contentStyle={styles.stateCardContent}>
            <PulsingMic />
            <Text style={styles.stateTitle}>Joining room…</Text>
            <ActivityIndicator color={colors.primaryLight} style={{ marginTop: spacing.sm }} />
          </GlassCard>
        </View>
      </View>
    );
  }

  if (error || !connection) {
    return (
      <View style={styles.stateRoot}>
        <ParticleBackground theme="dark" style={StyleSheet.absoluteFill} />
        <View style={styles.stateCenter}>
          <GlassCard mode="dark" style={styles.stateCard} contentStyle={styles.stateCardContent}>
            <Asset3D name="error" size={100} style={{ marginBottom: spacing.xs }} />
            <Text style={styles.stateTitle}>{error || 'Unable to connect to this room'}</Text>
            <GlassButton title="Retry" icon="refresh" onPress={fetchToken} style={{ marginTop: spacing.md }} />
            <TouchableOpacity onPress={goBack} style={{ marginTop: spacing.md }} accessibilityRole="button" accessibilityLabel="Go back">
              <Text style={styles.backLink}>Go back</Text>
            </TouchableOpacity>
          </GlassCard>
        </View>
      </View>
    );
  }

  // Connected: open the LiveKit session. Join audio-on, video-off; per-user
  // camera is opt-in via the camera toggle inside RoomBody.
  return (
    <LiveKitRoom
      serverUrl={connection.url}
      token={connection.token}
      connect={true}
      audio={true}
      video={false}
      onError={handleRoomError}
    >
      {/* BULLETPROOF: RoomBody renders LiveKit NATIVE views (VideoTrack, the
          screen-share picker, participant tiles). On RN 0.85 there is NO bridge
          fallback — if any of those native views throws while rendering under
          Fabric it would bubble to the ROOT ErrorBoundary and blank the WHOLE app
          ("something went wrong → takes you out"). Isolating RoomBody here means a
          render failure shows a recoverable in-room error, never a dead app. */}
      <Safe label="RoomBody" fallback={<RoomRenderFallback onLeave={goBack} />}>
        <RoomBody
          room={room}
          roomId={id}
          stage={stage}
          isOwner={isOwner}
          containerStyle={containerStyle}
          participantSize={participantSize}
          gridColumns={gridColumns}
          insets={insets}
        />
      </Safe>
    </LiveKitRoom>
  );
}

// Inner component: must live INSIDE <LiveKitRoom> so the room hooks resolve.
function RoomBody({
  room,
  roomId,
  stage,
  isOwner,
  containerStyle,
  participantSize,
  gridColumns,
  insets,
}: {
  room: RoomDetail | null;
  roomId: string;
  stage: boolean;
  isOwner: boolean;
  containerStyle: { maxWidth: number; alignSelf: 'center'; width: '100%' } | undefined;
  participantSize: number;
  gridColumns: number;
  insets: EdgeInsets;
}) {
  const navigation = useNavigation<Nav>();
  // Room/participant/track hooks resolve against the enclosing <LiveKitRoom>.
  const roomCtx = useRoomContext();
  const participants = useParticipants();
  // Subscribe to camera + screen-share tracks → TrackReference[].
  const videoTracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: false },
    { source: Track.Source.ScreenShare, withPlaceholder: false },
  ]);

  // The room is joined with `audio={true}` (mic PUBLISHED on connect — see the
  // <LiveKitRoom> mount), so the optimistic flag must start true or the button
  // desyncs from the actual publish state (shows muted while broadcasting).
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCamOn, setIsCamOn] = useState(false);
  const [togglingMic, setTogglingMic] = useState(false);
  const [togglingCam, setTogglingCam] = useState(false);

  // Tap-a-participant action sheet (mute-for-me / report).
  const [sheetParticipant, setSheetParticipant] = useState<Participant | null>(null);

  // WhatsApp-style "add participants" sheet (invite friends/following to this room).
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  const onInvited = useCallback((count: number) => {
    if (count > 0) {
      Alert.alert('Invites sent', `Invited ${count} ${count === 1 ? 'person' : 'people'} to the room.`);
    }
  }, []);

  // Screen-share state (see services/screenShare.ts for the start/stop contract).
  const [isSharing, setIsSharing] = useState(false);
  const [togglingShare, setTogglingShare] = useState(false);
  const screenIdentityRef = useRef<string | null>(null);
  const screenCaptureRef = useRef(null);
  // Actual LiveKit room name (custom_room_<id>) for backend screen-share signaling.
  const roomName = roomCtx?.name ?? '';

  // In-call emoji reactions (data-channel publish/receive + float animation).
  const { reactions, send: sendReaction, remove: removeReaction } = useReactions(roomCtx);

  // Drive the app-wide "call in progress — tap to return" banner while in the
  // room; cleared on any unmount/exit so it never lingers. This banner IS the
  // RN-safe PiP: minimizing (header chevron-down) leaves the room CONNECTED and
  // returns via its route/params.
  useEffect(() => {
    setActiveCall({
      kind: 'room',
      route: 'VoiceRoom',
      params: { id: room?.id ?? '' },
      title: room?.title || 'Voice room',
      subtitle: 'Voice room',
    });
  }, [room?.id, room?.title]);
  useEffect(() => () => { clearActiveCall(); }, []);

  // Defensive: hooks can momentarily return undefined during (re)connect.
  const safeParticipants = Array.isArray(participants) ? participants : [];

  const localIdentity = roomCtx?.localParticipant?.identity;

  // --- STAGE MODE (opt-in): listener vs speaker, request-to-speak, host approve ---
  // LiveKit is the source of truth for whether I can publish; it flips LIVE when
  // the host promotes me (RoomServiceClient.updateParticipant → no reconnect).
  const [permTick, setPermTick] = useState(0);
  useEffect(() => {
    if (!roomCtx) return;
    const onPerm = () => setPermTick((t) => t + 1);
    roomCtx.on(RoomEvent.ParticipantPermissionsChanged, onPerm as any);
    return () => { try { roomCtx.off(RoomEvent.ParticipantPermissionsChanged, onPerm as any); } catch {} };
  }, [roomCtx]);
  void permTick; // referenced so canPublish recomputes when permissions change
  const canPublish = roomCtx?.localParticipant?.permissions?.canPublish ?? true;
  const [handRaised, setHandRaised] = useState(false);

  const requestToSpeak = useCallback(async () => {
    setHandRaised(true);
    const res = await api(`/voice/rooms/${roomId}/raise-hand`, { method: 'POST' });
    if (res.success) {
      Alert.alert('Hand raised', 'The host has been asked to let you speak.');
    } else {
      setHandRaised(false);
      Alert.alert('Could not request', (res as any).error || 'Please try again.');
    }
  }, [roomId]);

  // Host: a listener raised a hand → offer to promote them to speaker.
  useEffect(() => {
    if (!stage || !isOwner) return;
    return onUserEvent('voice:handRaised', (p: any) => {
      if (!p || String(p.roomId) !== String(roomId) || !p.userId) return;
      const who = p.displayName || p.username || 'A listener';
      Alert.alert(`${who} wants to speak`, undefined, [
        { text: 'Not now', style: 'cancel' },
        { text: 'Allow', onPress: () => { void api(`/voice/rooms/${roomId}/speakers/${p.userId}/promote`, { method: 'POST' }); } },
      ]);
    });
  }, [stage, isOwner, roomId]);

  // My speaking permission changed (host promoted/demoted me) → LiveKit updates the
  // mic ability automatically; surface a toast + re-render the control.
  useEffect(() => {
    if (!stage) return;
    return onUserEvent('voice:speakerChanged', (p: any) => {
      if (!p || String(p.roomId) !== String(roomId)) return;
      setPermTick((t) => t + 1);
      const isMe = !!localIdentity && `user_${p.userId}` === localIdentity;
      if (isMe) {
        setHandRaised(false);
        Alert.alert(p.canPublish ? 'You can speak now' : 'Moved to listeners', p.canPublish ? 'Tap the mic to unmute.' : 'The host moved you back to the audience.');
      }
    });
  }, [stage, roomId, localIdentity]);

  // Map participant identity → their camera TrackReference (if publishing).
  // Lets each tile look up its video in O(1) instead of scanning the track list.
  // Also pull out the first ACTIVE screen share to feature above the grid.
  const { cameraByIdentity, featuredScreen } = useMemo(() => {
    const map = new Map<string, TrackReference>();
    let screen: TrackReference | undefined;
    const list = Array.isArray(videoTracks) ? videoTracks : [];
    for (const t of list) {
      if (!isTrackReference(t) || !t.participant?.identity) continue;
      if (t.source === Track.Source.ScreenShare) {
        if (!screen) screen = t as TrackReference;
        continue;
      }
      map.set(t.participant.identity, t as TrackReference);
    }
    return { cameraByIdentity: map, featuredScreen: screen };
  }, [videoTracks]);

  // Mic/cam toggles guard against re-entrancy (togglingX) and a missing local
  // participant, and only commit the optimistic UI flag after the SDK call
  // resolves so the button can't desync from the actual publish state.
  const handleToggleMic = useCallback(async () => {
    if (togglingMic || !roomCtx?.localParticipant) return;
    setTogglingMic(true);
    const next = !isMicOn;
    try {
      await roomCtx.localParticipant.setMicrophoneEnabled(next);
      setIsMicOn(next);
    } catch (e) {
      console.warn('[livekit] setMicrophoneEnabled failed:', e);
    } finally {
      setTogglingMic(false);
    }
  }, [togglingMic, isMicOn, roomCtx]);

  const handleToggleCam = useCallback(async () => {
    if (togglingCam || !roomCtx?.localParticipant) return;
    setTogglingCam(true);
    const next = !isCamOn;
    try {
      await roomCtx.localParticipant.setCameraEnabled(next);
      setIsCamOn(next);
    } catch (e) {
      console.warn('[livekit] setCameraEnabled failed:', e);
    } finally {
      setTogglingCam(false);
    }
  }, [togglingCam, isCamOn, roomCtx]);

  // Toggle screen sharing. iOS: trigger the system ReplayKit broadcast picker,
  // then publish the screen track on our existing connection. Backend start/stop
  // are best-effort peer signaling (services/screenShare.ts).
  const handleToggleScreenShare = useCallback(async () => {
    if (togglingShare || !roomCtx?.localParticipant) return;
    setTogglingShare(true);
    try {
      if (!isSharing) {
        if (Platform.OS === 'ios') {
          const reactTag = findNodeHandle(screenCaptureRef.current);
          if (reactTag != null) {
            // Optional-chained: manager is absent until the Broadcast Upload
            // Extension target ships → degrade gracefully, never crash.
            await NativeModules.ScreenCapturePickerViewManager?.show?.(reactTag);
          }
        }
        await roomCtx.localParticipant.setScreenShareEnabled(true);
        setIsSharing(true);
        const { identity } = await notifyScreenShareStart(roomName, { quality: 'medium', frameRate: 15 });
        screenIdentityRef.current = identity;
      } else {
        await roomCtx.localParticipant.setScreenShareEnabled(false);
        setIsSharing(false);
        await notifyScreenShareStop(roomName, screenIdentityRef.current);
        screenIdentityRef.current = null;
      }
    } catch (e) {
      console.warn('[livekit] screen share toggle failed:', e);
    } finally {
      setTogglingShare(false);
    }
  }, [togglingShare, isSharing, roomCtx, roomName]);

  // ---------------------------------------------------------------------------
  // SEQUENCED + TIMEOUT-GUARDED TEARDOWN (crash-on-close fix). The old code
  // stopped the audio session while <LiveKitRoom> was still disconnecting — the
  // known iOS double-stop / dealloc EXC_BAD_ACCESS family. Order is preserved:
  //   1. room.disconnect() and AWAIT it,
  //   2. short settle delay so the native audio engine fully stops,
  //   3. stopVoiceAudio() (single-shot guarded in the service),
  //   4. navigate away exactly ONCE.
  // runGuardedTeardown() wraps EVERY step in try/catch and races the whole
  // sequence against MAX_TEARDOWN_MS, so a hung disconnect/stop can no longer
  // freeze the screen — we force-navigate back regardless — and it always arms
  // scheduleVoiceAudioStop() so the audio session is guaranteed to deactivate.
  // Memoized promise so Leave button + header back/close + OS back can race
  // without double-disconnect / double-goBack.
  // ---------------------------------------------------------------------------
  const teardownPromiseRef = useRef<Promise<void> | null>(null);
  const tornDownRef = useRef(false);
  const navDoneRef = useRef(false);
  // Set when the user MINIMIZES (chevron-down → PiP): we pop the screen WITHOUT
  // running teardown so the LiveKit room stays connected and the app-wide
  // CallInProgressBanner offers a tap-to-return (the RN-safe PiP).
  const minimizingRef = useRef(false);
  // Drives the "Leaving room…" closing overlay so the user never sees a frozen
  // room (or a blank flash) while teardown runs.
  const [closing, setClosing] = useState(false);
  // Set when the HOST closed the room / we were remotely disconnected (not our
  // own teardown) → render the designed "Room ended" card instead of a generic
  // error/banner fall-through.
  const [ended, setEnded] = useState(false);

  const teardown = useCallback((): Promise<void> => {
    if (!teardownPromiseRef.current) {
      teardownPromiseRef.current = (async () => {
        await runGuardedTeardown(() => roomCtx?.disconnect());
        tornDownRef.current = true;
      })();
    }
    return teardownPromiseRef.current;
  }, [roomCtx]);

  const finishNav = useCallback((action?: any) => {
    if (navDoneRef.current) return;
    navDoneRef.current = true;
    try {
      if (action) navigation.dispatch(action);
      else navigation.goBack();
    } catch {}
  }, [navigation]);

  // Intercept EVERY removal (header back arrow, Android back, gesture, goBack)
  // and run the guarded teardown first; the pop proceeds once it's done (or the
  // timeout elapses). Show the closing overlay meanwhile. EXCEPTION: a minimize
  // (PiP) pops immediately and SKIPS teardown so the room stays connected.
  useEffect(() => {
    const unsub = (navigation as any).addListener('beforeRemove', (e: any) => {
      if (minimizingRef.current) return; // minimize-to-PiP: keep the room alive
      if (tornDownRef.current) return; // teardown complete — allow the pop
      e.preventDefault();
      setClosing(true);
      void teardown().then(() => finishNav(e.data?.action));
    });
    return unsub;
  }, [navigation, teardown, finishNav]);

  // Detect a HOST-closed room / remote disconnect. Guard with tornDownRef so our
  // own teardown-driven disconnect never trips the "ended" card.
  useEffect(() => {
    if (!roomCtx) return;
    const onDisconnected = () => {
      if (tornDownRef.current || minimizingRef.current) return;
      setEnded(true);
    };
    roomCtx.on(RoomEvent.Disconnected, onDisconnected as any);
    return () => { try { roomCtx.off(RoomEvent.Disconnected, onDisconnected as any); } catch {} };
  }, [roomCtx]);

  const doLeave = useCallback(async () => {
    setClosing(true);
    await teardown();
    finishNav();
  }, [teardown, finishNav]);

  // Confirm before leaving so an accidental tap doesn't drop the user.
  const onLeave = useCallback(() => {
    Alert.alert('Leave Room', 'Are you sure you want to leave this room?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Leave', style: 'destructive', onPress: () => { void doLeave(); } },
    ]);
  }, [doLeave]);

  // Minimize to PiP (header chevron-down): pop WITHOUT teardown so the LiveKit
  // room stays connected; the app-wide "call in progress" banner returns here.
  const minimizeToPip = useCallback(() => {
    minimizingRef.current = true;
    navigation.goBack();
  }, [navigation]);

  // Reserve scroll padding so the absolutely-positioned control bar never
  // overlaps the last grid row.
  const controlBarHeight = 80 + Math.max(insets.bottom, spacing.lg);
  // Grid scrolls UNDER the floating translucent header (Apple §12).
  const gridTopPad = insets.top + HEADER_HEIGHT + spacing.md;

  // One participant tile: live camera track if published, else an initials
  // avatar; overlays an interruptible speaking ring and a glass mute badge.
  const renderParticipant = ({ item }: { item: (typeof safeParticipants)[number] }) => {
    const identity = item?.identity ?? '';
    const displayName = item?.name || identity || 'User';
    const isLocal = identity && identity === localIdentity;
    const isSpeaking = !!item?.isSpeaking;
    const micEnabled = !!item?.isMicrophoneEnabled;
    const camTrack = identity ? cameraByIdentity.get(identity) : undefined;
    const roleColor = isLocal ? colors.warning : colors.primaryLight;
    // Avatar/video fills most of the (responsive) cell so wider tablet/desktop
    // tiles read as premium video cards, not a tiny 64px puck lost in whitespace.
    // Capped at 96 so the phone 3-up stays ~64.
    const avatarSize = Math.max(56, Math.min(96, participantSize - spacing.md * 2));
    const avatarRadius = avatarSize / 2;

    return (
      <TouchableOpacity
        style={[styles.participantCard, { width: participantSize }]}
        onPress={() => { if (!isLocal && item) setSheetParticipant(item); }}
        activeOpacity={isLocal ? 1 : 0.7}
        accessibilityRole="button"
        accessibilityLabel={isLocal ? `${displayName} (you)` : `Actions for ${displayName}`}
      >
        <View style={styles.participantAvatarWrap}>
          {/* Tile avatar sits on a dark glass material card so it reads as a
              premium participant tile, not a bare puck. */}
          <GlassSurface
            variant="card"
            mode="dark"
            radius={avatarRadius}
            sheen={false}
            style={!micEnabled ? { opacity: 0.72 } : undefined}
            contentStyle={{ padding: 0, overflow: 'hidden' }}
          >
            {camTrack ? (
              <VideoTrack
                trackRef={camTrack}
                // VideoTrack's style prop takes a single ViewStyle (not an array),
                // so flatten the base + responsive size overrides into one object.
                style={StyleSheet.flatten([
                  styles.participantVideo,
                  { width: avatarSize, height: avatarSize, borderRadius: avatarRadius },
                ])}
                objectFit="cover"
                mirror={!!isLocal}
              />
            ) : (
              <View
                style={[
                  styles.participantAvatarFallback,
                  { width: avatarSize, height: avatarSize, borderRadius: avatarRadius },
                ]}
              >
                <Text style={[styles.participantAvatarLetter, { fontSize: Math.round(avatarSize * 0.4) }]}>
                  {String(displayName).charAt(0).toUpperCase()}
                </Text>
              </View>
            )}
          </GlassSurface>
          {/* Interruptible speaking ring (reanimated) — no hard on/off pop. */}
          <SpeakingRing active={isSpeaking} radius={avatarRadius} />
          {!micEnabled && (
            <GlassSurface variant="card" mode="dark" radius={11} sheen={false} shadow={false} style={styles.muteIndicator} contentStyle={styles.muteIndicatorContent}>
              <Ionicons name="mic-off" size={11} color={colors.error} />
            </GlassSurface>
          )}
        </View>
        <Text style={[styles.participantName, { maxWidth: participantSize }]} numberOfLines={1}>
          {displayName}{isLocal ? ' (You)' : ''}
        </Text>
        <GlassSurface
          variant="pill"
          mode="dark"
          radius={radius.full}
          sheen={false}
          shadow={false}
          surfaceColor={roleColor + '22'}
          borderColor={roleColor + '33'}
          contentStyle={styles.roleBadgeContent}
        >
          <Text style={[styles.participantRole, { color: roleColor }]} numberOfLines={1}>
            {isLocal ? 'you' : 'speaker'}
          </Text>
        </GlassSurface>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      {/* Depth layer (all states): deep base behind drifting particles. */}
      <ParticleBackground theme="dark" style={StyleSheet.absoluteFill} />

      {/* Live connection state (connecting / reconnecting / dropped). */}
      <ConnectionBanner topOffset={insets.top + HEADER_HEIGHT + spacing.sm} />

      {/* Tap-a-participant actions (mute-for-me / report). */}
      <ParticipantSheet
        visible={!!sheetParticipant}
        participant={sheetParticipant}
        onClose={() => setSheetParticipant(null)}
      />

      {/* Add participants (invite friends/following to this room). */}
      <AddParticipantsSheet
        visible={addSheetOpen}
        roomId={room?.id ?? roomId}
        onClose={() => setAddSheetOpen(false)}
        onInvited={onInvited}
      />

      {/* The ReplayKit system broadcast picker (iOS) — must exist in the tree so
          ScreenCapturePickerViewManager.show(tag) can trigger it. Visually inert.
          Wrapped in <Safe>: this is a LEGACY (Paper) native view; if it fails to
          render under New Architecture it must NOT take down the whole room (that
          was the "something went wrong" that blanked the app). Screen-share picker
          degrades to unavailable; the room still loads. */}
      {Platform.OS === 'ios' && (
        <Safe label="ScreenCapturePickerView">
          <View style={styles.hiddenPicker} pointerEvents="none">
            <ScreenCapturePickerView ref={screenCaptureRef} />
          </View>
        </Safe>
      )}

      {/* Featured screen share (when anyone in the room is sharing). */}
      {featuredScreen ? (
        <View style={[styles.screenShareStage, containerStyle, { marginTop: gridTopPad }]}>
          <VideoTrack trackRef={featuredScreen} style={styles.screenShareVideo} objectFit="contain" />
          <GlassSurface variant="pill" mode="dark" radius={radius.full} sheen={false} style={styles.screenShareBadge} contentStyle={styles.screenShareBadgeContent}>
            <Ionicons name="phone-portrait-outline" size={12} color={colors.white} />
            <Text style={styles.screenShareBadgeText} numberOfLines={1}>
              {(featuredScreen.participant?.name || featuredScreen.participant?.identity || 'Someone')} is sharing
            </Text>
          </GlassSurface>
        </View>
      ) : null}

      {/* Participants Grid — scrolls UNDER the floating glass header. */}
      <FlatList
        // numColumns is not mutable in place — remount the list when the column
        // count changes (rotation / responsive breakpoint) so RN re-lays the grid.
        key={gridColumns}
        style={styles.grid}
        data={safeParticipants}
        keyExtractor={(item, index) => item?.identity ?? item?.sid ?? `participant-${index}`}
        renderItem={renderParticipant}
        numColumns={gridColumns}
        columnWrapperStyle={styles.gridRow}
        contentContainerStyle={[
          styles.gridContent,
          containerStyle,
          { paddingTop: featuredScreen ? spacing.md : gridTopPad, paddingBottom: controlBarHeight + 20 },
        ]}
        showsVerticalScrollIndicator={false}
        ListHeaderComponent={
          <View style={[styles.roomInfo, containerStyle]}>
            <View style={styles.roomPillRow}>
              <GlassSurface variant="pill" mode="dark" radius={radius.full} sheen={false} contentStyle={styles.livePillContent}>
                <LiveDot />
                <Text style={styles.livePillText}>LIVE</Text>
              </GlassSurface>
              <GlassSurface variant="pill" mode="dark" radius={radius.full} sheen={false} contentStyle={styles.countPillContent}>
                <Ionicons name="people" size={13} color={colors.faint} />
                <Text style={styles.countText}>
                  {safeParticipants.length} live
                </Text>
              </GlassSurface>
              {/* Live connection-quality pill (RTCStats-derived); self-hides when strong. */}
              <ConnectionQualityBadge />
            </View>
            <Text style={styles.roomTitle} numberOfLines={2}>{room?.title || 'Voice Room'}</Text>
            {room?.topic ? (
              <Text style={styles.roomTopic} numberOfLines={2}>{room.topic}</Text>
            ) : null}
          </View>
        }
        ListEmptyComponent={
          <View style={styles.emptyWrap}>
            <GlassCard mode="dark" style={styles.stateCard} contentStyle={styles.stateCardContent}>
              <PulsingMic />
              <Text style={styles.stateTitle}>Connecting to room…</Text>
            </GlassCard>
          </View>
        }
      />

      {/* FLOATING TRANSLUCENT HEADER (Apple §12): chevron-down = minimize to PiP,
          center = room title with an animated LIVE dot, X = confirmed leave.
          Content scrolls under it. */}
      <GlassSurface
        variant="bar"
        mode="dark"
        radius={0}
        sheen={false}
        style={[styles.floatingHeader, { paddingTop: insets.top }]}
        contentStyle={styles.floatingHeaderContent}
      >
        <TouchableOpacity
          onPress={minimizeToPip}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          accessibilityRole="button"
          accessibilityLabel="Minimize room"
        >
          <Ionicons name="chevron-down" size={26} color={colors.white} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <LiveDot />
          <Text style={styles.headerTitle} numberOfLines={1}>{room?.title || 'Voice Room'}</Text>
        </View>
        <TouchableOpacity
          onPress={onLeave}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          accessibilityRole="button"
          accessibilityLabel="Close room"
        >
          <Ionicons name="close" size={26} color={colors.white} />
        </TouchableOpacity>
      </GlassSurface>

      {/* Floating emoji reactions (rise + fade over the stage). */}
      <ReactionOverlay reactions={reactions} onDone={removeReaction} />

      {/* Emoji reactions row — sits just above the control bar. */}
      <View style={[styles.reactionsRowWrap, { bottom: controlBarHeight + spacing.sm }]} pointerEvents="box-none">
        <ReactionsRow onSend={sendReaction} />
      </View>

      {/* Bottom Controls — dark glass bar (Apple §12). Toggles are glass circles;
          active fills primary; the user's OWN mute is unmistakable; the
          destructive Leave is pushed to the row end past a divider gap. */}
      <GlassSurface
        variant="bar"
        mode="dark"
        radius={0}
        sheen={false}
        style={styles.controlBar}
        contentStyle={[styles.controlBarContent, { paddingBottom: Math.max(insets.bottom, spacing.lg) }]}
      >
        {/* Mic toggle — own-mute reads at a glance (red-tinted surface + icon). */}
        {stage && !canPublish ? (
          // Stage listener: no mic — request the mic instead (host approves).
          <GlassSurface
            mode="dark"
            radius={26}
            sheen={false}
            onPress={handRaised ? undefined : requestToSpeak}
            disabled={handRaised}
            surfaceColor={handRaised ? colors.primary + '22' : colors.primary}
            borderColor={colors.primary + '55'}
            style={styles.controlBtn}
            contentStyle={styles.controlBtnContent}
            accessibilityLabel="Request to speak"
          >
            <Ionicons name="hand-left" size={24} color={handRaised ? colors.primary : colors.white} />
          </GlassSurface>
        ) : (
          <GlassSurface
            mode="dark"
            radius={26}
            sheen={false}
            onPress={togglingMic ? undefined : handleToggleMic}
            disabled={togglingMic}
            surfaceColor={isMicOn ? colors.primary : colors.error + '22'}
            borderColor={isMicOn ? colors.primary : colors.error + '55'}
            style={styles.controlBtn}
            contentStyle={styles.controlBtnContent}
            accessibilityLabel={isMicOn ? 'Mute microphone' : 'Unmute microphone'}
          >
            <Ionicons name={isMicOn ? 'mic' : 'mic-off'} size={24} color={isMicOn ? colors.white : colors.error} />
          </GlassSurface>
        )}

        {/* Camera toggle */}
        <GlassSurface
          mode="dark"
          radius={26}
          sheen={false}
          onPress={togglingCam ? undefined : handleToggleCam}
          disabled={togglingCam}
          surfaceColor={isCamOn ? colors.primary : undefined}
          style={styles.controlBtn}
          contentStyle={styles.controlBtnContent}
          accessibilityLabel={isCamOn ? 'Turn camera off' : 'Turn camera on'}
        >
          <Ionicons name={isCamOn ? 'videocam' : 'videocam-off'} size={22} color={isCamOn ? colors.white : colors.faint} />
        </GlassSurface>

        {/* Screen share toggle */}
        <GlassSurface
          mode="dark"
          radius={26}
          sheen={false}
          onPress={togglingShare ? undefined : handleToggleScreenShare}
          disabled={togglingShare}
          surfaceColor={isSharing ? colors.primary : undefined}
          style={styles.controlBtn}
          contentStyle={styles.controlBtnContent}
          accessibilityLabel={isSharing ? 'Stop sharing screen' : 'Share screen'}
        >
          <Ionicons name="phone-portrait-outline" size={22} color={isSharing ? colors.white : colors.faint} />
        </GlassSurface>

        {/* Add participants (WhatsApp-style invite) */}
        <GlassSurface
          mode="dark"
          radius={26}
          sheen={false}
          onPress={() => setAddSheetOpen(true)}
          style={styles.controlBtn}
          contentStyle={styles.controlBtnContent}
          accessibilityLabel="Add participants"
        >
          <Ionicons name="person-add" size={22} color={colors.faint} />
        </GlassSurface>

        {/* Divider gap — separates the destructive Leave from the toggles. */}
        <View style={styles.controlDivider} />

        {/* Leave — destructive, visually distinct from the toggle row. */}
        <GlassSurface
          mode="dark"
          radius={radius.full}
          sheen={false}
          onPress={closing ? undefined : onLeave}
          disabled={closing}
          surfaceColor={colors.error}
          borderColor={colors.error}
          style={styles.leaveBtn}
          contentStyle={styles.leaveBtnContent}
          accessibilityLabel="Leave room"
        >
          <Ionicons name="exit-outline" size={20} color={colors.white} />
          <Text style={styles.leaveBtnText}>Leave</Text>
        </GlassSurface>
      </GlassSurface>

      {/* ENDED STATE: host closed the room / remote disconnect. A designed end
          card over the stage instead of a silent fall-through to a generic error. */}
      {ended && (
        <View style={styles.overlayCenter} pointerEvents="auto">
          <GlassCard mode="dark" style={styles.stateCard} contentStyle={styles.stateCardContent}>
            <Ionicons name="radio-outline" size={44} color={colors.primaryLight} />
            <Text style={styles.stateTitle}>Room ended</Text>
            <Text style={styles.stateSub}>The host ended this room.</Text>
            <GlassButton title="Back to Live" onPress={() => navigation.goBack()} style={{ marginTop: spacing.md }} />
          </GlassCard>
        </View>
      )}

      {/* CLOSING STATE: covers the live room UI while the guarded teardown runs
          so the user never sees a frozen room or a blank flash on the way out.
          Same dark glass material as every other state. */}
      {closing && (
        <View style={styles.overlayCenter} pointerEvents="auto">
          <GlassCard mode="dark" style={styles.stateCard} contentStyle={styles.stateCardContent}>
            <ActivityIndicator size="large" color={colors.primaryLight} />
            <Text style={styles.stateTitle}>Leaving room…</Text>
          </GlassCard>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  // Deep base BEHIND the particles (not the flat surface) — depth continuity.
  container: {
    flex: 1,
    backgroundColor: colorTokens.dark.bg,
  },
  // Pre-connect (joining / error) roots share the same depth base.
  stateRoot: {
    flex: 1,
    backgroundColor: colorTokens.dark.bg,
  },
  stateCenter: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xxxl,
  },
  overlayCenter: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xxxl,
    backgroundColor: colorTokens.dark.bg + 'CC',
  },
  stateCard: {
    minWidth: 240,
    maxWidth: 360,
  },
  stateCardContent: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xl,
    paddingHorizontal: spacing.xl,
  },
  stateTitle: {
    ...typographyScale.body,
    color: colors.white,
    textAlign: 'center',
    ...fonts.semibold,
  },
  stateSub: {
    ...typographyScale.caption,
    color: colors.faint,
    textAlign: 'center',
    ...fonts.regular,
  },
  backLink: {
    fontSize: 15,
    color: colors.primaryLight,
    ...fonts.semibold,
  },
  // ── Floating translucent header ──
  floatingHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 20,
  },
  floatingHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    height: HEADER_HEIGHT,
  },
  headerCenter: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs + 2,
    paddingHorizontal: spacing.sm,
  },
  headerTitle: {
    ...typographyScale.h3,
    color: colors.white,
    flexShrink: 1,
    ...fonts.semibold,
  },
  // ── Room info (list header) ──
  roomInfo: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
  },
  roomPillRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  livePillContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: 4,
  },
  livePillText: {
    ...typographyScale.caption,
    color: colors.primaryLight,
    ...fonts.bold,
  },
  countPillContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: 4,
  },
  countText: {
    ...typographyScale.caption,
    ...tabularNums,
    color: colors.faint,
    ...fonts.medium,
  },
  roomTitle: {
    ...typographyScale.h2,
    color: colors.white,
    ...fonts.bold,
  },
  roomTopic: {
    ...typographyScale.body,
    color: colors.faint,
    marginTop: spacing.xs,
    ...fonts.regular,
  },
  // The ReplayKit picker is driven programmatically; keep it off-screen + inert.
  hiddenPicker: {
    position: 'absolute',
    width: 0,
    height: 0,
    opacity: 0,
  },
  screenShareStage: {
    marginHorizontal: spacing.lg,
    height: 200,
    borderRadius: radius.lg,
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  screenShareVideo: {
    width: '100%',
    height: '100%',
  },
  screenShareBadge: {
    position: 'absolute',
    top: spacing.sm,
    left: spacing.sm,
    maxWidth: '80%',
  },
  screenShareBadgeContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
  },
  screenShareBadgeText: {
    ...typographyScale.caption,
    color: colors.white,
    ...fonts.medium,
  },
  gridRow: {
    justifyContent: 'flex-start',
    paddingHorizontal: spacing.lg,
    marginTop: spacing.lg,
    gap: spacing.md,
  },
  grid: {
    flex: 1,
  },
  gridContent: {
    flexGrow: 1,
  },
  participantCard: {
    alignItems: 'center',
  },
  participantAvatarWrap: {
    position: 'relative',
    marginBottom: spacing.sm,
  },
  participantVideo: {
    width: 64,
    height: 64,
    borderRadius: 32,
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  participantAvatarFallback: {
    backgroundColor: colors.primary + '30',
    alignItems: 'center',
    justifyContent: 'center',
  },
  participantAvatarLetter: {
    fontSize: 24,
    color: colors.primaryLight,
    ...fonts.bold,
  },
  speakingRing: {
    position: 'absolute',
    top: -4,
    left: -4,
    right: -4,
    bottom: -4,
    borderRadius: 36,
    borderWidth: 3,
    borderColor: colors.success,
  },
  muteIndicator: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 22,
    height: 22,
  },
  muteIndicatorContent: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  participantName: {
    ...typographyScale.caption,
    fontSize: 12,
    color: colors.white,
    textAlign: 'center',
    ...fonts.medium,
  },
  roleBadgeContent: {
    marginTop: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
  },
  participantRole: {
    fontSize: 10,
    textTransform: 'capitalize',
    ...fonts.semibold,
  },
  // ── Control bar (dark glass) ──
  controlBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  controlBarContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    gap: spacing.lg,
  },
  controlBtn: {
    width: 52,
    height: 52,
  },
  controlBtnContent: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
  },
  controlDivider: {
    width: 1,
    height: 32,
    backgroundColor: 'rgba(255,255,255,0.12)',
    marginHorizontal: spacing.xs,
  },
  leaveBtn: {
    // pushed to the row end, visually separated from the toggles
  },
  leaveBtnContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  leaveBtnText: {
    fontSize: 14,
    color: colors.white,
    ...fonts.bold,
  },
  emptyWrap: {
    alignItems: 'center',
    paddingTop: 40,
  },
  // ── Emoji reactions ──
  reactionOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 160,
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: 300,
  },
  risingEmoji: {
    position: 'absolute',
    bottom: 0,
    fontSize: 34,
  },
  reactionsRowWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  reactionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  reactionBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  reactionEmoji: {
    fontSize: 24,
  },
});
